import { jsxs, jsx } from 'react/jsx-runtime';
import cns from '../../../node_modules/classnames/index.js';
import { useFloating, useHover, safePolygon, useFocus, useDismiss, useRole, useInteractions, FloatingPortal } from '../../../node_modules/@floating-ui/react/dist/floating-ui.react.js';
import { useState, useCallback, useEffect } from 'react';
import useTooltipHoverWithDelay from '../Hooks/useTooltipHoverWithDelay.js';
import { Position } from '../../types.js';
import TooltipContent from './TooltipContent.js';
import { autoUpdate } from '../../../node_modules/@floating-ui/dom/dist/floating-ui.dom.js';
import { offset, flip, shift } from '../../../node_modules/@floating-ui/react-dom/dist/floating-ui.react-dom.js';

/**
Il componente avvolge un qualsiasi componente aggiungendo un tooltip che compare all'hover.
Se lo stato è uncontrolled, il tooltip funziona al hover dei children.

Il contenuto deve avere tra le 9 e le 12 parole per riga.

Utilities:

- `Tooltip.useTooltipHoverWithDelay`: Questo componente gestite lo stato di un componente Tooltip ritardando la chiusura di delay millisecondi. Deve essere utilizzato quando il contenuto del tooltip ha dei componenti attivi, ad esempio un link, e il delay deve essere scelto per dare il tempo all'utente di agire.
*/
const TooltipNew = function Tooltip({ className = undefined, contentClassName = undefined, children, content, position = Position.bottom, type = undefined, show = undefined, onClose = undefined, onMouseEnter = undefined, onMouseLeave = undefined, }) {
    const showOnHover = show === undefined || show === null;
    const [isOpen, setIsOpen] = useState(show || true);
    const onCloseFloating = useCallback(() => {
        setIsOpen(false);
        if (onClose) {
            onClose();
        }
    }, [onClose]);
    useEffect(() => {
        setIsOpen(show || false);
    }, [show]);
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        placement: position,
        onOpenChange: showOnHover ? setIsOpen : undefined,
        // Make sure the tooltip stays on the screen
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(8),
            flip({
                fallbackAxisSideDirection: 'start',
            }),
            shift(),
        ],
    });
    // Event listeners to change the open state
    const hover = useHover(context, {
        move: false,
        handleClose: safePolygon({
            blockPointerEvents: true,
        }),
    });
    const focus = useFocus(context);
    const dismiss = useDismiss(context);
    // Role props for screen readers
    const role = useRole(context, { role: 'tooltip' });
    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        focus,
        dismiss,
        role,
    ]);
    return (jsxs("div", Object.assign({ className: cns('v2-tooltip', 'floating-ui', { [`v2-tooltip--${type}`]: !!type }, className), onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, ref: refs.setReference }, getReferenceProps(), { children: [jsx(FloatingPortal, { children: isOpen && (jsx("div", Object.assign({ className: cns('v2-tooltip', 'v2-tooltip__portal', { [`v2-tooltip--${type}`]: !!type }, contentClassName), ref: refs.setFloating, style: floatingStyles }, getFloatingProps(), { children: content && (jsx(TooltipContent, { onClose: showOnHover ? undefined : onCloseFloating, children: content })) }))) }), children] })));
};
TooltipNew.useTooltipHoverWithDelay = useTooltipHoverWithDelay;
TooltipNew.Content = TooltipContent;

export { TooltipNew as default };
